import React, { useState, useEffect } from 'react';
import { getDietaryProducts, getDietaryProductMeals, getCustomerDietaryRecommendations, createCustomerDietaryRecommendation, deleteCustomerDietaryRecommendation, getCustomerPurchasedProducts, createCustomerPurchasedProduct, updateCustomerPurchasedProductStatus, deleteCustomerPurchasedProduct, getCustomerMealRecords } from '../api';

const DIETARY_TYPES = {
  '7day': '7天膳食包',
  '21day': '21天减脂餐',
  'meal': '代餐系列',
  'tea': '养生茶饮',
  'supplement': '营养补充'
};

const DIETARY_TYPE_CONFIG = {
  '7day': { icon: '📦', color: '#FF6600' },
  '21day': { icon: '🥗', color: '#4CAF50' },
  'meal': { icon: '🍱', color: '#FF9800' },
  'tea': { icon: '🍵', color: '#8BC34A' },
  'supplement': { icon: '💊', color: '#2196F3' }
};

function DietaryManagement({ customer, onClose, user }) {
  const [activeTab, setActiveTab] = useState('recommended'); // 'recommended' | 'available' | 'purchased'
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [purchasedProducts, setPurchasedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [recommendPriority, setRecommendPriority] = useState(1); // 默认优先级1
  const [recommendNotes, setRecommendNotes] = useState(''); // 推荐备注
  const [loading, setLoading] = useState(false);
  const [showPurchasedModal, setShowPurchasedModal] = useState(false);
  const [purchasedFormData, setPurchasedFormData] = useState({
    product_id: '',
    purchase_date: new Date().toISOString().split('T')[0],
    start_date: new Date().toISOString().split('T')[0],
    total_days: '7',
    notes: ''
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [selectedProductDetail, setSelectedProductDetail] = useState(null);
  const [showPackageDetailModal, setShowPackageDetailModal] = useState(false);
  const [packageDetailRecords, setPackageDetailRecords] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [statusFormData, setStatusFormData] = useState({
    status: 'paused',
    pause_reason: '',
    notes: ''
  });
  const [editingPurchasedId, setEditingPurchasedId] = useState(null);

  useEffect(() => {
    loadData();
  }, [customer]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 加载所有膳食产品
      const [productsRes, recommendedRes, purchasedRes] = await Promise.all([
        getDietaryProducts({}),
        getCustomerDietaryRecommendations(customer.id),
        getCustomerPurchasedProducts(customer.id)
      ]);

      if (productsRes.data.success) {
        const allProducts = productsRes.data.data || [];

        // 区分已推荐和可推荐的产品
        if (recommendedRes.data.success) {
          const recommended = recommendedRes.data.data || [];
          setRecommendedProducts(recommended);

          // 可推荐的产品 = 所有产品 - 已推荐产品
          const recommendedProductIds = recommended.map(r => r.product_id);
          const available = allProducts.filter(p => !recommendedProductIds.includes(p.id));
          setAvailableProducts(available);
        }
      }

      if (purchasedRes.data.success) {
        setPurchasedProducts(purchasedRes.data.data || []);
      }
    } catch (error) {
      console.error('加载膳食数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecommend = async () => {
    if (selectedProducts.length === 0) {
      alert('请至少选择一个产品进行推荐');
      return;
    }

    setLoading(true);
    try {
      await createCustomerDietaryRecommendation(customer.id, {
        product_ids: selectedProducts,
        priority: recommendPriority,
        notes: recommendNotes
      });

      // 重新加载数据
      await loadData();
      setSelectedProducts([]);
      setRecommendPriority(1);
      setRecommendNotes('');
      setActiveTab('recommended');
      alert('推荐成功！');
    } catch (error) {
      alert('推荐失败：' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecommend = async (productId) => {
    if (!window.confirm('确定要取消推荐吗？')) return;

    try {
      await deleteCustomerDietaryRecommendation(customer.id, productId);
      await loadData();
      alert('取消推荐成功！');
    } catch (error) {
      alert('操作失败：' + (error.response?.data?.message || error.message));
    }
  };

  const handleAddPurchased = () => {
    if (activeTab === 'recommended' && selectedProducts.length > 0) {
      // 从已推荐中选择，直接填充第一个产品
      const selectedId = selectedProducts[0];
      setPurchasedFormData({
        product_id: selectedId,
        purchase_date: new Date().toISOString().split('T')[0],
        start_date: new Date().toISOString().split('T')[0],
        total_days: '7',
        notes: ''
      });
      setShowPurchasedModal(true);
    } else {
      // 手动添加购买记录 - 可以选择所有可用产品
      setShowPurchasedModal(true);
    }
  };

  const handleSavePurchased = async () => {
    if (!purchasedFormData.product_id) {
      alert('请选择产品');
      return;
    }

    setLoading(true);
    try {
      if (editingPurchasedId) {
        // 更新购买记录（实际上是更新状态）
        await updateCustomerPurchasedProductStatus(editingPurchasedId, {
          status: purchasedFormData.status,
          pause_reason: purchasedFormData.pause_reason,
          notes: purchasedFormData.notes
        });
      } else {
        // 创建新的购买记录
        await createCustomerPurchasedProduct({
          customer_id: customer.id,
          product_id: parseInt(purchasedFormData.product_id),
          start_date: purchasedFormData.start_date,
          total_days: parseInt(purchasedFormData.total_days),
          notes: purchasedFormData.notes,
          status: 'active'
        });
      }

      await loadData();
      setShowPurchasedModal(false);
      setEditingPurchasedId(null);
      setPurchasedFormData({
        product_id: '',
        purchase_date: new Date().toISOString().split('T')[0],
        start_date: new Date().toISOString().split('T')[0],
        total_days: '7',
        notes: ''
      });
      alert('保存成功！');
    } catch (error) {
      alert('保存失败：' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (purchased, action) => {
    const statusMap = {
      'pause': 'paused',
      'resume': 'active',
      'complete': 'completed',
      'delete': 'deleted'
    };

    if (action === 'delete') {
      if (!window.confirm('确定要删除这条购买记录吗？')) return;
      try {
        await deleteCustomerPurchasedProduct(purchased.id);
        await loadData();
        alert('删除成功！');
      } catch (error) {
        alert('删除失败：' + (error.response?.data?.message || error.message));
      }
      return;
    }

    if (action === 'pause') {
      setStatusFormData({
        status: statusMap[action],
        pause_reason: '',
        notes: ''
      });
      setEditingPurchasedId(purchased.id);
      setShowStatusModal(true);
      return;
    }

    try {
      await updateCustomerPurchasedProductStatus(purchased.id, {
        status: statusMap[action],
        pause_reason: '',
        notes: action === 'complete' ? '提前结束' : '恢复执行'
      });
      await loadData();
      alert('操作成功！');
    } catch (error) {
      alert('操作失败：' + (error.response?.data?.message || error.message));
    }
  };

  // 查看膳食包详情
  const handleViewPackageDetail = async (purchased) => {
    setSelectedPackage(purchased);
    setPackageDetailRecords({});
    setShowPackageDetailModal(true);

    try {
      // 获取所有餐次模板
      const mealsRes = await getDietaryProductMeals(purchased.product_id);
      // 获取已使用的记录
      const recordsRes = await getCustomerMealRecords(customer.id, { purchased_product_id: purchased.id });

      const allMeals = mealsRes.data?.data || [];
      const usedRecords = recordsRes.data?.data || [];

      // 构建已使用记录的key Set
      const usedSet = new Set(usedRecords.map(r => `${r.day_number}-${r.meal_type}`));

      // 按日期分组
      const grouped = {};
      allMeals.forEach(meal => {
        const dayKey = `第${meal.day_number}天`;
        if (!grouped[dayKey]) {
          grouped[dayKey] = [];
        }
        const isUsed = usedSet.has(`${meal.day_number}-${meal.meal_type}`);
        const usedRecord = usedRecords.find(r => r.day_number === meal.day_number && r.meal_type === meal.meal_type);
        grouped[dayKey].push({
          ...meal,
          is_used: isUsed,
          completion_rate: usedRecord?.completion_rate || 0,
          is_completed: isUsed ? 1 : 0
        });
      });
      setPackageDetailRecords(grouped);
    } catch (error) {
      console.error('加载膳食包详情失败:', error);
    }
  };

  const handleSelectProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const calculateProgress = (purchased) => {
    if (!purchased) return { percent: 0, text: '未开始' };

    const { total_days, status, completed_meals } = purchased;

    // 总餐次数 = 总天数 × 3餐（早餐、午餐、晚餐）
    const totalMeals = (total_days || 0) * 3;
    // 已完成餐次数（从后端API获取）
    const completed = completed_meals || 0;

    const percent = totalMeals > 0 ? Math.round((completed / totalMeals) * 100) : 0;

    let text = '';
    if (status === 'active') {
      text = `进行中 (${completed}/${totalMeals}餐)`;
    } else if (status === 'paused') {
      text = `已暂停 (${completed}/${totalMeals}餐)`;
    } else if (status === 'completed') {
      text = '已完成';
    }

    return { percent, text };
  };

  const getProductInfo = (productId) => {
    // 先从推荐产品中查找（推荐产品有product_id字段）
    let product = recommendedProducts.find(p => p.product_id === productId);
    if (product) {
      // 推荐产品数据是平铺的，直接返回字段映射
      return {
        id: product.product_id,
        title: product.product_title,
        description: product.description,
        price: product.price,
        icon: product.icon,
        product_type: product.product_type
      };
    }

    // 再从可用产品中查找
    product = availableProducts.find(p => p.id === productId);
    if (product) {
      return product;
    }

    return {};
  };

  return (
    <div className="modal-mask">
      <div className="modal-box modal-wide" onClick={e => e.stopPropagation()}>
        <div className="modal-title">
          <span>膳食管理 - {customer.name}</span>
          <span className="modal-close" onClick={onClose}>×</span>
        </div>

        <div className="modal-content">
          {/* 客户信息 */}
          <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>
            <div><strong>客户：</strong>{customer.name} ({customer.phone})</div>
            <div><strong>体质：</strong>{customer.health_status || '未填写'}</div>
            <div><strong>减重目标：</strong>{customer.target_weight ? `目标体重 ${customer.target_weight}kg` : '未设置'}</div>
          </div>

          {/* 标签页切换 */}
          <div className="detail-tabs" style={{ marginBottom: '16px' }}>
            <div
              className={`detail-tab ${activeTab === 'recommended' ? 'active' : ''}`}
              onClick={() => { setActiveTab('recommended'); setSelectedProducts([]); }}
            >
              已推荐膳食 ({recommendedProducts.length})
            </div>
            <div
              className={`detail-tab ${activeTab === 'available' ? 'active' : ''}`}
              onClick={() => { setActiveTab('available'); setSelectedProducts([]); }}
            >
              可推荐膳食 ({availableProducts.length})
            </div>
            <div
              className={`detail-tab ${activeTab === 'purchased' ? 'active' : ''}`}
              onClick={() => { setActiveTab('purchased'); setSelectedProducts([]); }}
            >
              已购买膳食 ({purchasedProducts.length})
            </div>
          </div>

          {loading ? (
            <div className="loading-text">加载中...</div>
          ) : (
            <>
              {/* 已推荐膳食标签页 */}
              {activeTab === 'recommended' && (
                <div>
                  {recommendedProducts.length === 0 ? (
                    <div className="empty-tip">
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                      <div style={{ fontSize: '16px', marginBottom: '8px', color: '#666' }}>暂无已推荐膳食</div>
                      <div style={{ fontSize: '14px', color: '#999' }}>请从"可推荐膳食"标签页选择产品进行推荐</div>
                    </div>
                  ) : (
                    <div>
                      {recommendedProducts.map(item => {
                        const config = DIETARY_TYPE_CONFIG[item.product_type] || { icon: '📦', color: '#666' };
                        return (
                          <div key={item.id} style={{
                            border: '1px solid #e8e8e8',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '12px',
                            background: '#fff'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '24px' }}>{config.icon}</span>
                                <div>
                                  <div style={{ fontWeight: '500', fontSize: '15px' }}>{item.product_title}</div>
                                  <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                                    {DIETARY_TYPES[item.product_type]} | {item.description || ''}
                                  </div>
                                </div>
                              </div>
                              <button
                                className="btn-xs btn-red"
                                onClick={() => handleDeleteRecommend(item.product_id)}
                              >
                                取消推荐
                              </button>
                            </div>
                            {item.notes && (
                              <div style={{ marginTop: '12px', padding: '8px 12px', background: '#f0f9f0', borderRadius: '4px', fontSize: '13px', color: '#52c41a' }}>
                                💡 {item.notes}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* 可推荐膳食标签页 */}
              {activeTab === 'available' && (
                <div>
                  {availableProducts.length === 0 ? (
                    <div className="empty-tip">
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                      <div style={{ fontSize: '16px', marginBottom: '8px', color: '#666' }}>暂无可推荐膳食</div>
                      <div style={{ fontSize: '14px', color: '#999' }}>所有膳食产品都已推荐</div>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          已选择 <strong>{selectedProducts.length}</strong> 个产品
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn-xs btn-blue"
                            onClick={() => setSelectedProducts(availableProducts.map(p => p.id))}
                          >
                            全选
                          </button>
                          <button
                            className="btn-xs btn-gray"
                            onClick={() => setSelectedProducts([])}
                          >
                            清空
                          </button>
                        </div>
                      </div>

                      {availableProducts.map(product => {
                        const config = DIETARY_TYPE_CONFIG[product.product_type] || { icon: '📦', color: '#666' };
                        const isSelected = selectedProducts.includes(product.id);
                        return (
                          <div
                            key={product.id}
                            style={{
                              border: isSelected ? '2px solid #1890ff' : '1px solid #e8e8e8',
                              borderRadius: '8px',
                              padding: '16px',
                              marginBottom: '12px',
                              background: isSelected ? '#f0f9ff' : '#fff',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => handleSelectProduct(product.id)}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '24px' }}>{config.icon}</span>
                                <div>
                                  <div style={{ fontWeight: '500', fontSize: '15px' }}>{product.title}</div>
                                  <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                                    {DIETARY_TYPES[product.product_type]} | {product.description || ''}
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#999', marginTop: '4px' }}>
                                    ¥{product.price}
                                  </div>
                                </div>
                              </div>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                border: isSelected ? '2px solid #1890ff' : '2px solid #d9d9d9',
                                background: isSelected ? '#1890ff' : 'transparent'
                              }} />
                            </div>
                          </div>
                        );
                      })}

                      {selectedProducts.length > 0 && (
                        <div style={{
                          position: 'sticky',
                          bottom: '0',
                          background: '#fff',
                          padding: '16px 0',
                          borderTop: '1px solid #e8e8e8',
                          marginTop: '16px'
                        }}>
                          {/* 优先级和备注设置 */}
                          <div style={{ marginBottom: '12px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <label style={{ fontSize: '13px', color: '#666' }}>推荐优先级：</label>
                              <select
                                className="form-input"
                                style={{ width: '80px', padding: '6px' }}
                                value={recommendPriority}
                                onChange={e => setRecommendPriority(parseInt(e.target.value))}
                              >
                                <option value={1}>主推</option>
                                <option value={0}>常规</option>
                                <option value={2}>活动</option>
                              </select>
                            </div>
                            <div style={{ flex: 1 }}>
                              <input
                                type="text"
                                className="form-input"
                                style={{ padding: '6px' }}
                                value={recommendNotes}
                                onChange={e => setRecommendNotes(e.target.value)}
                                placeholder="推荐理由/个性化备注（可选）"
                              />
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <button className="btn-xs btn-gray" onClick={() => setActiveTab('purchased')}>
                              或添加到已购买
                            </button>
                            <button className="action-btn btn-blue" onClick={handleRecommend} disabled={loading}>
                              {loading ? '推荐中...' : `确认推荐 (${selectedProducts.length})`}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* 已购买膳食标签页 */}
              {activeTab === 'purchased' && (
                <div>
                  <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      管理 <strong>{purchasedProducts.length}</strong> 个购买记录
                    </div>
                    <button className="btn-xs btn-blue" onClick={handleAddPurchased}>
                      + 添加购买记录
                    </button>
                  </div>

                  {purchasedProducts.length === 0 ? (
                    <div className="empty-tip">
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                      <div style={{ fontSize: '16px', marginBottom: '8px', color: '#666' }}>暂无已购买膳食</div>
                      <div style={{ fontSize: '14px', color: '#999' }}>请从推荐列表选择产品或手动添加购买记录</div>
                    </div>
                  ) : (
                    <div>
                      {purchasedProducts.map(purchased => {
                        const config = DIETARY_TYPE_CONFIG[purchased.product_type] || { icon: '📦', color: '#666' };
                        const progress = calculateProgress(purchased);

                        return (
                          <div key={purchased.id} style={{
                            border: '1px solid #e8e8e8',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '12px',
                            background: '#fff'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '28px' }}>{config.icon}</span>
                                <div>
                                  <div style={{ fontWeight: '500', fontSize: '16px' }}>{purchased.title}</div>
                                  <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                                    {DIETARY_TYPES[purchased.product_type]} | ¥{purchased.price}
                                  </div>
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', fontSize: '13px', color: '#666' }}>
                                <div>购买：{purchased.purchase_date}</div>
                                <div>开始：{purchased.start_date}</div>
                              </div>
                            </div>

                            {/* 进度条 */}
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '4px',
                                fontSize: '13px'
                              }}>
                                <span>{progress.text}</span>
                                <span>{progress.percent}%</span>
                              </div>
                              <div style={{
                                height: '8px',
                                background: '#f0f0f0',
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  height: '100%',
                                  width: `${progress.percent}%`,
                                  background: progress.percent >= 100 ? '#52c41a' : '#1890ff',
                                  transition: 'width 0.3s'
                                }} />
                              </div>
                            </div>

                            {/* 操作按钮 */}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button className="btn-xs btn-blue" onClick={() => handleViewPackageDetail(purchased)}>
                                查看详情
                              </button>
                              {purchased.status === 'active' && (
                                <>
                                  <button className="btn-xs btn-orange" onClick={() => handleStatusChange(purchased, 'pause')}>
                                    暂停
                                  </button>
                                  <button className="btn-xs btn-green" onClick={() => handleStatusChange(purchased, 'complete')}>
                                    提前结束
                                  </button>
                                </>
                              )}
                              {purchased.status === 'paused' && (
                                <button className="btn-xs btn-blue" onClick={() => handleStatusChange(purchased, 'resume')}>
                                  恢复
                                </button>
                              )}
                              <button className="btn-xs btn-red" onClick={() => handleStatusChange(purchased, 'delete')}>
                                删除
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 添加购买记录弹窗 */}
      {showPurchasedModal && (
        <div className="modal-mask">
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              <span>{editingPurchasedId ? '编辑购买记录' : '添加购买记录'}</span>
              <span className="modal-close" onClick={() => setShowPurchasedModal(false)}>×</span>
            </div>
            <div className="modal-content">
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">选择产品 *</label>
                  <select
                    className="form-input"
                    value={purchasedFormData.product_id}
                    onChange={e => setPurchasedFormData({...purchasedFormData, product_id: e.target.value})}
                  >
                    <option value="">请选择产品</option>
                    {[...availableProducts, ...recommendedProducts].map(item => {
                      // 处理不同的数据结构
                      let id, title, price, productType;

                      if (item.product_title) {
                        // 来自推荐列表的结构
                        id = item.product_id;
                        title = item.product_title;
                        price = item.price;
                        productType = item.product_type;
                      } else {
                        // 直接的产品数据
                        id = item.id;
                        title = item.title;
                        price = item.price;
                        productType = item.product_type;
                      }

                      const config = DIETARY_TYPE_CONFIG[productType] || { icon: '📦' };
                      return (
                        <option key={id} value={id}>
                          {config.icon} {title} - ¥{price}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">购买日期 *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={purchasedFormData.purchase_date}
                    onChange={e => setPurchasedFormData({...purchasedFormData, purchase_date: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">开始日期 *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={purchasedFormData.start_date}
                    onChange={e => setPurchasedFormData({...purchasedFormData, start_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">总天数 *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={purchasedFormData.total_days}
                    onChange={e => setPurchasedFormData({...purchasedFormData, total_days: e.target.value})}
                    placeholder="如：7天填7，21天填21"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">备注</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={purchasedFormData.notes}
                    onChange={e => setPurchasedFormData({...purchasedFormData, notes: e.target.value})}
                    placeholder="购买备注、客户特殊要求等"
                  />
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button onClick={() => setShowPurchasedModal(false)}>取消</button>
              <button className="action-btn btn-blue" onClick={handleSavePurchased} disabled={loading}>
                {loading ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 状态管理弹窗 */}
      {showStatusModal && (
        <div className="modal-mask">
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              <span>状态管理</span>
              <span className="modal-close" onClick={() => setShowStatusModal(false)}>×</span>
            </div>
            <div className="modal-content">
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">暂停原因</label>
                  <select
                    className="form-input"
                    value={statusFormData.pause_reason}
                    onChange={e => setStatusFormData({...statusFormData, pause_reason: e.target.value})}
                  >
                    <option value="">请选择原因</option>
                    <option value="客户出差">客户出差</option>
                    <option value="身体不适">身体不适</option>
                    <option value="吃腻了">吃腻了</option>
                    <option value="其他原因">其他原因</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">详细备注</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={statusFormData.notes}
                    onChange={e => setStatusFormData({...statusFormData, notes: e.target.value})}
                    placeholder="其他说明或备注信息"
                  />
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button onClick={() => setShowStatusModal(false)}>取消</button>
              <button className="action-btn btn-blue" onClick={handleSavePurchased}>
                确认暂停
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 膳食包详情弹窗 */}
      {showPackageDetailModal && selectedPackage && (
        <div className="modal-mask">
          <div className="modal-box modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              <span>{selectedPackage.title} - 餐次详情</span>
              <span className="modal-close" onClick={() => setShowPackageDetailModal(false)}>×</span>
            </div>
            <div className="modal-content" style={{maxHeight: '70vh', overflowY: 'auto'}}>
              <div style={{marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '8px'}}>
                <div style={{fontSize: '14px', color: '#666'}}>
                  {selectedPackage.total_days}天 · {DIETARY_TYPES[selectedPackage.product_type]}
                </div>
                <div style={{fontSize: '12px', color: '#999', marginTop: '4px'}}>
                  开始日期：{selectedPackage.start_date} · 购买日期：{selectedPackage.purchase_date}
                </div>
              </div>

              {Object.keys(packageDetailRecords).length === 0 ? (
                <div style={{textAlign: 'center', padding: '40px', color: '#999'}}>
                  暂无餐次记录
                </div>
              ) : (
                Object.keys(packageDetailRecords).map(date => {
                  const records = packageDetailRecords[date];
                  return (
                    <div key={date} style={{marginBottom: '16px'}}>
                      <div style={{fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#333'}}>
                        {date}
                      </div>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        {records.map((record, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px',
                            background: record.is_used ? '#f6ffed' : '#fff',
                            border: `1px solid ${record.is_used ? '#b7eb8f' : '#e8e8e8'}`,
                            borderRadius: '6px'
                          }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <span style={{fontSize: '16px'}}>
                                {record.meal_type === 'breakfast' ? '🌅' : record.meal_type === 'lunch' ? '☀️' : record.meal_type === 'dinner' ? '🌙' : '🍪'}
                              </span>
                              <span style={{fontWeight: '500'}}>
                                {record.meal_type === 'breakfast' ? '早餐' : record.meal_type === 'lunch' ? '午餐' : record.meal_type === 'dinner' ? '晚餐' : '加餐'}
                              </span>
                            </div>
                            <div style={{textAlign: 'right'}}>
                              <div style={{color: record.is_used ? '#52c41a' : '#999', fontWeight: '500'}}>
                                {record.is_used ? '✓ 已选择' : '○ 未选择'}
                              </div>
                              {record.is_used && record.completion_rate > 0 && (
                                <div style={{fontSize: '12px', color: '#666'}}>
                                  完成度：{record.completion_rate}%
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="modal-foot">
              <button onClick={() => setShowPackageDetailModal(false)}>关闭</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DietaryManagement;
